/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import CheckCircleOutlineIconLegacy from '@atlaskit/icon/glyph/check-circle-outline';
import CheckCircleOutlineIcon from '@atlaskit/icon/utility/check-circle';
import ShareIcon from '@atlaskit/icon/core/migration/share';
import InlineDialog from '@atlaskit/inline-dialog';
import Textfield from '@atlaskit/textfield';
import { G400, N10 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	DROPDOWN,
} from '@atlassian/analytics-bridge';
import OriginTracing from '@atlassiansox/origin-tracing';

import type { PageTreeItemProperties } from '../../types';

import messages from './messages';
import { Content, InlineMessage, Subtitle, Title } from './styled';

interface Props {
	page: PageTreeItemProperties;
	onToggle?: (pageId: string | null) => void;
	stickyRowContentId?: string | null;
}

const ShareButton = ({ page, onToggle, stickyRowContentId }: Props) => {
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const isOpen = stickyRowContentId === page.id;

	const { formatMessage } = useIntl();
	// the page-tree gets re-rendered every time a share is clicked so we have to create a new origin here,
	// otherwise the actual generated ID would be behind by 1 render
	const origin = useMemo(() => new OriginTracing({ product: 'jira' }), []);

	const handleCopy = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			navigator.clipboard.writeText(origin.addToUrl(page.url)).then(() => {
				setIsCopied(true);
				fireUIAnalytics(analyticsEvent, 'copyPageLinkButton', origin.toAnalyticsAttributes());
			});
		},
		[origin, page.url],
	);

	const handleShareClick = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'sharePageButton', {
				// we negate isOpen since state in parent hasn't been updated yet
				isOpen: !isOpen,
				...origin.toAnalyticsAttributes({
					hasGeneratedId: !isOpen,
				}), // start an origin trace when the share toggle is opened
			});
			onToggle?.(isOpen ? null : page.id);
			if (isOpen) {
				setIsCopied(false);
			}
		},
		[page, isOpen, origin, onToggle],
	);

	// need to use different callback as we don't get the same props from inline dialog as we do from button
	const handleShareClose = useCallback(() => {
		onToggle?.(null);
		setIsCopied(false);
	}, [onToggle]);

	const textFieldOverrideStyles = css({
		backgroundColor: token('color.background.input', N10),
	});

	const dialogContent = (
		<div>
			<ContextualAnalyticsData sourceName="sharePage" sourceType={DROPDOWN}>
				<Title>{formatMessage(messages.title)}</Title>
				<Subtitle>{formatMessage(messages.subtitle)}</Subtitle>
				<Content>
					<Textfield
						isCompact
						name="link"
						isReadOnly
						value={origin.addToUrl(page.url)}
						appearance="subtle"
						css={textFieldOverrideStyles}
					/>
					<Button onClick={handleCopy}>{formatMessage(messages.copyButton)}</Button>
				</Content>
				{isCopied && (
					<InlineMessage>
						<CheckCircleOutlineIcon
							LEGACY_fallbackIcon={CheckCircleOutlineIconLegacy}
							color={token('color.icon.success', G400)}
							LEGACY_size="small"
							label="copied"
						/>
						{formatMessage(messages.copied)}
					</InlineMessage>
				)}
				<FireScreenAnalytics attributes={origin.toAnalyticsAttributes()} />
			</ContextualAnalyticsData>
		</div>
	);

	return (
		<Tooltip content={formatMessage(messages.tooltip)} position="top">
			<InlineDialog
				content={dialogContent}
				isOpen={isOpen}
				placement="bottom-end"
				onClose={handleShareClose}
			>
				<IconButton
					appearance="subtle"
					icon={ShareIcon}
					isSelected={isOpen}
					onClick={handleShareClick}
					testId="confluence-project-pages.page-tree.page-controls.share-button"
					label="share"
				/>
			</InlineDialog>
		</Tooltip>
	);
};

export default ShareButton;
